/**
 * Rob Steins: charms fall into the bag from the top of the screen whenever the cart slider opens
 * (clicking the cart icon, or the slider opening by itself after something is added).
 *
 * The photos of the charms in the bag fall together with a few illustrated charms and land in the slider with a
 * little bounce and a sparkle before sinking in. It is drawn in a layer inside the slider's dialog (the only place
 * that can sit above a modal), never blocks clicks, and is skipped for people who prefer reduced motion.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const COLORS = ['#e2587a', '#9b7bd8', '#e8c45a', '#6fc3df', '#f29bb2', '#5fae7a'];
const SHAPES = [
  'M50 84C20 64 12 46 22 34c9-11 23-7 28 3 5-10 19-14 28-3 10 12 2 30-28 50Z', // heart
  'M50 30l7 15 16 2-12 11 3 16-14-8-14 8 3-16-12-11 16-2z', // star
  'M50 30c12 16 22 27 22 38a22 22 0 0 1-44 0c0-11 10-22 22-38Z', // drop
  'M60 32a26 26 0 1 0 14 44 21 21 0 1 1-14-44Z', // moon
  'M26 40h48a8 8 0 0 1 8 8v22a8 8 0 0 1-8 8H26a8 8 0 0 1-8-8V48a8 8 0 0 1 8-8Z', // spacer
];

/** @param {number} n */
const pick = (n) => Math.floor(Math.random() * n);

/** A small silver-ringed charm, like the illustrations used elsewhere in the theme. */
function svgCharm() {
  const d = SHAPES[pick(SHAPES.length)];
  const color = COLORS[pick(COLORS.length)];
  return `<svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true"><circle cx="50" cy="14" r="8" fill="none" stroke="#b9b5c4" stroke-width="4"/><rect x="46" y="20" width="8" height="12" rx="3" fill="#b9b5c4"/><path d="${d}" fill="${color}" stroke="#b9b5c4" stroke-width="2.5"/></svg>`;
}

let lastPlayed = 0;

/** @param {HTMLDialogElement} dialog */
function play(dialog) {
  if (reduceMotion.matches || !dialog.open) return;
  const now = Date.now();
  if (now - lastPlayed < 1500) return;
  lastPlayed = now;

  // Where the slider will sit once it has finished sliding in
  const width = dialog.getBoundingClientRect().width || Math.min(480, window.innerWidth);
  const left = Math.max(0, window.innerWidth - width - (window.innerWidth >= 750 ? 12 : 0));

  const photos = [...dialog.querySelectorAll('.cart-items__table-row:not(.rs-addon-line) .cart-items__media-image')]
    .map((img) => (img instanceof HTMLImageElement ? img.currentSrc || img.src : ''))
    .filter(Boolean);

  const layer = document.createElement('div');
  layer.className = 'rs-fall';
  layer.setAttribute('aria-hidden', 'true');
  dialog.append(layer);

  // a gentle drop: a few charms, not a shower
  const count = window.innerWidth < 750 ? 5 : 7;
  const reach = Math.min(window.innerHeight * 0.42, 380);
  let longest = 0;

  for (let i = 0; i < count; i++) {
    const size = 40 + Math.random() * 22;
    const el = document.createElement('span');
    el.className = 'rs-fall__c';

    // every other charm is a real photo from the bag (when there is one), the rest are illustrations
    if (photos.length && i % 2 === 0) {
      el.classList.add('is-photo');
      const img = document.createElement('img');
      img.src = photos[(i / 2) % photos.length];
      img.alt = '';
      el.append(img);
    } else {
      el.innerHTML = svgCharm();
    }

    const x = left + 20 + Math.random() * Math.max(10, width - 40 - size);
    const landY = 150 + Math.random() * reach;
    const delay = i * 140 + Math.random() * 120; // one after another
    const duration = 1600 + Math.random() * 300;
    const spin = () => `${Math.round(-15 + Math.random() * 30)}deg`;
    el.style.cssText = `left:${x.toFixed(0)}px;width:${size.toFixed(0)}px;height:${size.toFixed(0)}px;`;
    layer.append(el);

    el.animate(
      [
        { transform: `translateY(${-size - 20}px) rotate(${spin()}) scale(0.9)`, opacity: 0, easing: 'cubic-bezier(0.45, 0, 0.55, 1)' },
        { transform: `translateY(${landY * 0.2}px) rotate(${spin()}) scale(1)`, opacity: 1, offset: 0.15, easing: 'cubic-bezier(0.45, 0, 0.3, 1)' },
        { transform: `translateY(${landY}px) rotate(${spin()}) scale(1)`, opacity: 1, offset: 0.7, easing: 'cubic-bezier(0.4, 0, 0.6, 1)' },
        { transform: `translateY(${landY - 6}px) rotate(0deg) scale(1)`, opacity: 1, offset: 0.8 },
        { transform: `translateY(${landY + 10}px) rotate(0deg) scale(0.6)`, opacity: 0 },
      ],
      { duration, delay, fill: 'both' }
    );

    // a little star pops where each charm lands
    setTimeout(() => {
      const spark = document.createElement('i');
      spark.className = 'rs-fall__spark';
      spark.style.cssText = `left:${(x + size * 0.7).toFixed(0)}px;top:${(landY + size * 0.2).toFixed(0)}px;`;
      layer.append(spark);
      spark.animate(
        [
          { opacity: 0, transform: 'scale(0) rotate(0deg)' },
          { opacity: 1, transform: 'scale(1.3) rotate(60deg)', offset: 0.4 },
          { opacity: 0, transform: 'scale(0.5) rotate(150deg)' },
        ],
        { duration: 650, easing: 'ease-out', fill: 'both' }
      );
    }, delay + duration * 0.7);

    longest = Math.max(longest, delay + duration);
  }

  setTimeout(() => layer.remove(), longest + 1200);
}

function watch() {
  const dialog = document.querySelector('#cart-drawer dialog');
  if (!(dialog instanceof HTMLDialogElement)) return;
  new MutationObserver(() => {
    // wait until the slider has slid in and its items have settled, so the two don't move at the same time
    if (dialog.open) setTimeout(() => play(dialog), 650);
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch, { once: true });
else watch();
